<template>
  <div 
    v-if="idleWarningShown"
    class="modal-parent"
  >
    <div class="inactivity-modal">
      <div
        id="inactivity-modal-title"
        data-testid="inactivity-modal-title"
        class="inactivity-modal__title">
        Is anybody out there?
      </div>
      <div
        id="inactivity-modal-message"
        data-testid="inactivity-modal-message"
        class="inactivity-modal__message">
        You are about to be logged out due to inactivity. Please click here to stay logged in. You will be logged out automatically in 
        <strong> {{ remainingTime }}s </strong>
      </div>
      <div class="inactivity-modal__footer">
        <button
          id="cancel-inactivity-modal-button"
          data-testid="cancel-inactivity-modal-button"
          class="btn btn--grey"
          @click.once="cleanupAndLogout"
        >
          Sign out
        </button>
        <button
          id="accept-inactivity-modal-button"
          data-testid="accept-inactivity-modal-button"
          class="btn btn--green"
          @click.once="stayLoggedIn"
        >
          Stay Logged In
        </button>
      </div>
    </div>
    <div class="modal-overlay" />
  </div>
</template>

<script>
import constants from '../global/constants'
import storageMixin from '../mixins/storageMixin'

export default {
  name: 'InactivityTimeout',
  props: {
    idleTimeout: {
      type: Number,
    },
    idleWarningTime: {
      type: Number,
    }
  },
  mixins: [ storageMixin ],
  data() {
    return {
      idleWarningShown: false,
      idleInterval: null,
      idleTime: 0,
    }
  },
  mounted() {
    this.startIdleTimeout()
    
    // Storage event listener set on a browser window to detect a logout from
    // another session (ie. tab or window) and auto logout all other sessions
    window.addEventListener('storage', this.storageEventHandler)
  },
  beforeDestroy() {
    this.cleanup()
    this.removeLogoutListeners()
  },
  computed: {
    remainingTime() {
      return this.idleTimeout - this.idleTime
    },
  },
  methods: {
    startIdleTimeout() {
      this.cleanup()
      this.updateActiveSession()

      // Listen to user activity events
      document.addEventListener('scroll', this.updateActiveSession)
      document.addEventListener('keypress', this.updateActiveSession)
      document.addEventListener('click', this.updateActiveSession)

      // Check idle time at regular intervals
      this.idleInterval = setInterval(this.checkIdleTime, 1000) // Check every second
    },
    updateActiveSession() {
      this.setStorage(constants.storageKeys.LastSessionActive, Date.now())
    },
    storageEventHandler(event) {
      if (event.key == constants.storageKeys.Logout) {
        this.removeLogoutListeners()
        this.cleanupAndLogout()
        this.displayAlert('fail', 'Logged out all sessions.')
      } else if (event.key == constants.storageKeys.LastSessionActive) {
        this.idleTime = 0
      }
    },
    toggleWarningModal() {
      this.idleWarningShown = !this.idleWarningShown
    },
    checkIdleTime() {
      // Show the warning when idleTime hits the warning threshold
      const lastActiveMs = this.getStorage(constants.storageKeys.LastSessionActive)
      this.idleTime = Math.round((Date.now() - lastActiveMs) / 1000) // convert to seconds

      if (this.idleTime >= this.idleTimeout - this.idleWarningTime && !this.idleWarningShown) {
        this.removeIdleListeners()
        this.toggleWarningModal()
      } else if (this.idleTime < this.idleTimeout - this.idleWarningTime && this.idleWarningShown) {
        // This handles the case where idle warning is already being shown on one tab, and a new tab is
        // opened. The idle time is reset and the warning modal should be hidden.
        this.idleWarningShown = false
      }

      // Log out the user when idleTime exceeds maxIdleTime
      if (this.idleTime >= this.idleTimeout) {
        this.cleanupAndLogout()
        this.displayAlert('fail', 'Logged out due to inactivity.')
      }
    },
    cleanup() {
      clearInterval(this.idleInterval)
      this.removeIdleListeners()
      
      // Ensure the warning modal is turned off and idleTime is reset
      this.idleWarningShown = false
    },
    cleanupAndLogout() {
      this.cleanup()
      this.logout()
    },
    stayLoggedIn() {
      this.startIdleTimeout()
    },
    removeLogoutListeners() {
      window.removeEventListener('storage', this.storageEventHandler)
    },
    removeIdleListeners() {
      document.removeEventListener('scroll', this.updateActiveSession)
      document.removeEventListener('keypress', this.updateActiveSession)
      document.removeEventListener('click', this.updateActiveSession)
    },
  }
}

</script>

<style lang="scss" scoped>


.inactivity {
  &-modal {
    @include flex-display();
    @include flex-direction(column);
    @include flex-justify-content(space-around);
    @include flex-align-items(center);
    position: fixed;
    top: 25%;
    left: 40vw;
    box-sizing: border-box;
    background: $white;
    border-radius: 5px;
    padding: 1em;
    z-index: 1020;
    width: 500px;
    min-height: 300px;
    box-shadow: 0px 6px 10px 1px rgba(0, 0, 0, 0.2);
    @media #{$mobile} {
      width: 100%;
      height: 100%;
      left: 0;
      top: 0;
      margin-top: 0;
      box-sizing: border-box;
    }
    &__title {
      font-size: $title-font;
      font-weight: 600;
      color: $black;
      text-align: center;
    }
    &__message {
      color: $dark-grey;
      text-align: center;
      line-height: 1.8em;
      margin-top: 1em;
      margin-bottom: 2em;
    }
    &__footer {
      @include flex-display();
      @include flex-justify-content(space-evenly);
      width: 100%;
      @media #{$mobile} {
        @include flex-justify-content(space-between);
      }
    }
  }
}
</style>